/**
 * Runtime gate for project-persistent opportunity groups.
 *
 * TypeScript types do not exist at runtime. A stale/tampered proxy response, a
 * foreign project or a group with unknown fields must never be rendered as team
 * data. One invalid item fails the entire page closed.
 */

import type {
  OpportunityGroup,
  OpportunityGroupCreateInput,
  OpportunityGroupPage,
  OpportunityGroupUpdateInput,
} from "../api/client";

export interface OpportunityGroupsLoader {
  listOpportunityGroups(
    projectId: string,
    options?: { cursor?: string | null; limit?: number },
  ): Promise<OpportunityGroupPage>;
  createOpportunityGroup(
    projectId: string,
    input: OpportunityGroupCreateInput,
  ): Promise<OpportunityGroup>;
  updateOpportunityGroup(
    projectId: string,
    groupId: string,
    input: OpportunityGroupUpdateInput,
  ): Promise<OpportunityGroup>;
}

const GROUP_SCHEMA_VERSION = "r5.opportunity_group.v1";
const GROUP_ID = /^ogp_[0-9a-f]{32}$/;
const PROJECT_ID = /^prj_[0-9a-z_]+$/;
const USER_ID = /^usr_[0-9a-z_]+$/;
const JOB_ID = /^job_[0-9a-z_]+$/;
const ORG_ID = /^org_[0-9a-z_]+$/;
const DIGEST = /^sha256:[0-9a-f]{64}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const CURSOR = /^(?:[0-9]|[1-9][0-9])$/;
const MAX_NAME = 80;
const MAX_NOTE = 2000;
const MAX_ORGANIZATIONS = 20;
const MAX_PAGE_ITEMS = 50;
const MAX_VERSION = 1_000_000;

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOwn(value: UnknownRecord, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function hasExactKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  return Object.keys(value).length === keys.length && keys.every((key) => hasOwn(value, key));
}

function isBoundedText(value: unknown, maximum: number, minimum = 1): value is string {
  return typeof value === "string" && value.length >= minimum && value.length <= maximum;
}

function isName(value: unknown): value is string {
  return isBoundedText(value, MAX_NAME) && value.trim().length > 0;
}

function isDate(value: unknown): value is string {
  if (typeof value !== "string" || !DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function isDateTime(value: unknown): value is string {
  return (
    isBoundedText(value, 40) &&
    DATE_TIME.test(value) &&
    !Number.isNaN(Date.parse(value))
  );
}

function isDigest(value: unknown): value is string {
  return typeof value === "string" && DIGEST.test(value);
}

function isInteger(value: unknown, minimum: number, maximum: number): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= minimum &&
    value <= maximum
  );
}

function isOrganizationIds(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length >= 1 &&
    value.length <= MAX_ORGANIZATIONS &&
    value.every((entry) => typeof entry === "string" && entry.length <= 64 && ORG_ID.test(entry)) &&
    new Set(value).size === value.length
  );
}

const SOURCE_KEYS = [
  "job_id",
  "decisions_hash",
  "analysis_output_hash",
  "review_output_hash",
  "snapshot_hash",
  "workflow",
  "evaluated_on",
] as const;

function isSource(value: unknown): boolean {
  if (!isRecord(value) || !hasExactKeys(value, SOURCE_KEYS)) return false;
  if (
    !isBoundedText(value.job_id, 64) ||
    !JOB_ID.test(value.job_id) ||
    !isDigest(value.decisions_hash) ||
    !isDigest(value.analysis_output_hash) ||
    !isDigest(value.review_output_hash) ||
    !isDigest(value.snapshot_hash) ||
    !isDate(value.evaluated_on)
  ) {
    return false;
  }
  const workflow = value.workflow;
  return (
    isRecord(workflow) &&
    hasExactKeys(workflow, ["key", "version", "content_hash"]) &&
    workflow.key === "megabooker.opportunity_analysis" &&
    workflow.version === 1 &&
    isDigest(workflow.content_hash)
  );
}

const GROUP_KEYS = [
  "schema_version",
  "id",
  "project_id",
  "name",
  "note",
  "organization_ids",
  "source",
  "created_by",
  "created_at",
  "updated_by",
  "updated_at",
  "version",
] as const;

/** Validate one group and bind it to the selected project. */
export function parseOpportunityGroup(
  value: unknown,
  expectedProjectId: string,
): OpportunityGroup | null {
  if (!isRecord(value) || !hasExactKeys(value, GROUP_KEYS)) return null;
  if (value.schema_version !== GROUP_SCHEMA_VERSION) return null;
  if (
    typeof value.id !== "string" ||
    !GROUP_ID.test(value.id) ||
    typeof value.project_id !== "string" ||
    !PROJECT_ID.test(value.project_id) ||
    value.project_id !== expectedProjectId
  ) {
    return null;
  }
  if (!isName(value.name) || !isBoundedText(value.note, MAX_NOTE, 0)) return null;
  if (!isOrganizationIds(value.organization_ids) || !isSource(value.source)) return null;
  if (
    typeof value.created_by !== "string" ||
    !USER_ID.test(value.created_by) ||
    !isDateTime(value.created_at) ||
    typeof value.updated_by !== "string" ||
    !USER_ID.test(value.updated_by) ||
    !isDateTime(value.updated_at) ||
    !isInteger(value.version, 1, MAX_VERSION)
  ) {
    return null;
  }
  return value as unknown as OpportunityGroup;
}

/** Validate a bounded page; every item must belong to the selected project. */
export function parseOpportunityGroupPage(
  value: unknown,
  expectedProjectId: string,
): OpportunityGroupPage | null {
  if (!isRecord(value) || !hasExactKeys(value, ["items", "next_cursor"])) return null;
  if (!Array.isArray(value.items) || value.items.length > MAX_PAGE_ITEMS) return null;
  const items: OpportunityGroup[] = [];
  for (const raw of value.items) {
    const parsed = parseOpportunityGroup(raw, expectedProjectId);
    if (parsed === null) return null;
    items.push(parsed);
  }
  if (
    value.next_cursor !== null &&
    (typeof value.next_cursor !== "string" || !CURSOR.test(value.next_cursor))
  ) {
    return null;
  }
  return { items, next_cursor: value.next_cursor as string | null };
}

const MAX_GROUPS = 100;
const MAX_PAGES = 4;

/**
 * Read the complete bounded team list. The API page is bounded at 50 and the
 * aggregate at 100, so follow cursors but fail closed on a repeated cursor, an
 * oversized response or more pages than the aggregate bound allows.
 *
 * `isCurrent` lets a caller abandon the continuation between pages when its own
 * project/generation guard has moved on. Without it the call resolves the list.
 */
export async function loadAllOpportunityGroups(
  loader: OpportunityGroupsLoader,
  projectId: string,
): Promise<OpportunityGroup[]>;
export async function loadAllOpportunityGroups(
  loader: OpportunityGroupsLoader,
  projectId: string,
  isCurrent: () => boolean,
): Promise<OpportunityGroup[] | null>;
export async function loadAllOpportunityGroups(
  loader: OpportunityGroupsLoader,
  projectId: string,
  isCurrent?: () => boolean,
): Promise<OpportunityGroup[] | null> {
  const loaded: OpportunityGroup[] = [];
  const loadedIds = new Set<string>();
  const usedCursors = new Set<string>();
  let cursor: string | null = null;
  for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex += 1) {
    if (isCurrent && !isCurrent()) return null;
    const raw = await loader.listOpportunityGroups(projectId, { cursor, limit: 50 });
    if (isCurrent && !isCurrent()) return null;
    const parsed = parseOpportunityGroupPage(raw, projectId);
    if (parsed === null) throw new Error("group_response_invalid");
    for (const group of parsed.items) {
      if (loadedIds.has(group.id)) throw new Error("group_response_duplicate");
      loadedIds.add(group.id);
    }
    loaded.push(...parsed.items);
    if (loaded.length > MAX_GROUPS) throw new Error("group_response_overflow");
    const next = parsed.next_cursor;
    if (!next) break;
    if (usedCursors.has(next)) throw new Error("group_cursor_repeat");
    usedCursors.add(next);
    cursor = next;
    if (pageIndex === MAX_PAGES - 1) throw new Error("group_page_limit");
  }
  return loaded;
}

/** Short, non-authoritative source label for the group card. */
export function opportunityGroupSourceLabel(group: OpportunityGroup): string {
  return `izvor ${group.source.job_id} · evaluirano ${group.source.evaluated_on}`;
}
